import {
    DashboardAccessLevelFilter,
    DashboardList,
    IDashboard,
    RequestPostDashboard,
} from '../services/api/dashboard/types';
import {
    addDashboard,
    copyDashboard,
    deleteDashboard,
    getDashboardById,
    getDashboardsList,
    patchDashboardFavorite,
    patchDashboardSortOrder,
    updateDashboardLayout,
    updateDashboardTitle,
} from '../services/api/dashboard/dashboardApi';
import { computed, ref } from 'vue';
import {
    deleteDashboardWidget,
    updateDashboardWidgetComment,
} from '../services/api/dashboard/dashboardWidgetApi';
import { useRoute, useRouter } from 'vue-router';

import axios from 'axios';
import { defineStore } from 'pinia';
import { AccessLevel } from '../services/api/shareDashboard/types';

export type DashboardStore = ReturnType<typeof useDashboardStore>;

export const useDashboardStore = defineStore('dashboard', () => {
    const items = ref<DashboardList>([]);
    const widgetFormOpen = ref<string | undefined>(undefined);
    const isLoadingDashboard = ref(false);
    const isLoadingItems = ref(true);
    const isRefetching = ref(false);
    const dashboards = ref<Record<string, IDashboard>>({});
    const selectedDashboardId = computed(() => route.query.id?.toString() ?? undefined);
    const selectedDashboard = computed(() => dashboards.value[selectedDashboardId.value ?? '']);

    const isDashboardOwner = computed(() => {
        return selectedDashboard.value?.accessLevel === AccessLevel.Owner;
    });

    const route = useRoute();
    const router = useRouter();

    let currentDashboardAbortController: AbortController | null = null;
    let currentDashboardRequestId = 0;

    const getItems = async function (accessLevel?: DashboardAccessLevelFilter) {
        isLoadingItems.value = true;
        try {
            const response = await getDashboardsList(accessLevel);
            items.value = [...response.dashboards];
        } catch (error) {
            console.error('Error fetching dashboard list:', error);
            items.value = [];
        } finally {
            isLoadingItems.value = false;
        }
    };

    const persistSortOrder = async function (orderedItems: DashboardList) {
        try {
            await patchDashboardSortOrder({
                items: orderedItems.map((item, sortOrder) => ({
                    dashboardId: item.id,
                    sortOrder,
                })),
            });
        } catch (error) {
            console.error('Error persisting dashboard sort order:', error);
        }
    };

    const addItem = async function (newDashboard: RequestPostDashboard) {
        const response = await addDashboard(newDashboard);

        const createdDashboard: IDashboard = {
            ...response,
            accessLevel: AccessLevel.Owner,
        };

        const reordered = [
            {
                title: createdDashboard.title,
                id: createdDashboard.id,
                accessLevel: AccessLevel.Owner,
                isFavorite: false,
                sortOrder: 0,
            },
            ...items.value,
        ].map((item, index) => ({ ...item, sortOrder: index }));
        items.value = reordered;
        const dashboardId = createdDashboard.id;
        dashboards.value[dashboardId] = createdDashboard;
        router.replace({
            query: { ...route.query, id: dashboardId },
        });
        await persistSortOrder(reordered);
    };

    const copyItem = async function (dashboardId: string, title: string) {
        const response = await copyDashboard({ dashboardId, title });

        const copiedDashboard: IDashboard = {
            ...response,
            accessLevel: AccessLevel.Owner,
        };

        const reordered = [
            {
                title: copiedDashboard.title,
                id: copiedDashboard.id,
                accessLevel: AccessLevel.Owner,
                isFavorite: false,
                sortOrder: 0,
            },
            ...items.value,
        ].map((item, index) => ({ ...item, sortOrder: index }));
        items.value = reordered;
        dashboards.value[copiedDashboard.id] = copiedDashboard;
        router.replace({
            query: { ...route.query, id: copiedDashboard.id },
        });
        await persistSortOrder(reordered);
    };

    const getItem = async function (dashboardId: string) {
        currentDashboardRequestId += 1;
        const requestId = currentDashboardRequestId;

        currentDashboardAbortController?.abort();
        currentDashboardAbortController = new AbortController();

        isLoadingDashboard.value = true;

        try {
            // Phase 1: dashboard + grid + widget metadata, graph widgets without their data.
            const response = await getDashboardById(
                dashboardId,
                currentDashboardAbortController.signal
            );

            if (requestId !== currentDashboardRequestId) {
                return;
            }

            dashboards.value[dashboardId] = response;
            // Phase 2 (per graph widget) is driven by the widget components themselves: each fetches
            // its own data on mount via the dashboard widget endpoint. See GridChart.vue.
            return response;
        } catch (error) {
            if (requestId !== currentDashboardRequestId) {
                return;
            }

            if (axios.isCancel(error)) {
                return;
            }

            if (
                axios.isAxiosError(error) &&
                (error.code === 'ERR_CANCELED' || error.message === 'canceled')
            ) {
                return;
            }

            if (axios.isAxiosError(error)) {
                const fallbackId = items.value[0]?.id;
                if (
                    error.response?.status === 404 &&
                    fallbackId &&
                    selectedDashboardId.value === dashboardId
                ) {
                    router.replace({ query: { ...route.query, id: fallbackId } });
                }
            }

            throw error;
        } finally {
            if (requestId === currentDashboardRequestId) {
                isLoadingDashboard.value = false;
            }
        }
    };

    const deleteItem = async function (dashboardId: string) {
        isRefetching.value = true;
        try {
            await deleteDashboard({ id: dashboardId });
            items.value = items.value.filter((item) => item.id !== dashboardId);
            delete dashboards.value[dashboardId];
            isRefetching.value = false;
            router.replace({
                query: { ...route.query, id: items.value[0].id },
            });
        } catch (error) {
            console.error('Error deleting dashboard:', error);
        }
    };

    const updateItemTitle = async function (dashboardId: string, updatedTitle: string) {
        const response = await updateDashboardTitle(updatedTitle, dashboardId);
        items.value = items.value.map((item) => {
            if (item.id === response.id) {
                item.title = response.title;
            }
            return item;
        });

        if (dashboards.value[response.id]) {
            const selectedItem = {
                ...dashboards.value[response.id],
                title: response.title,
            } as IDashboard;

            dashboards.value[dashboardId] = { ...selectedItem, title: response.title };
        }

        return dashboards.value[response.id];
    };

    const updateDashboardWidgetLayout = async function (
        widgetLayout: IDashboard['widgetLayout'],
        dashboardId: string
    ) {
        if (!dashboards.value[dashboardId]) {
            return;
        }
        try {
            const res = await updateDashboardLayout({
                id: dashboardId,
                widgetLayout,
            });

            dashboards.value[dashboardId].widgetLayout = widgetLayout;
            return res;
        } catch (error) {
            console.error('Error updating dashboard widget layout:', error);
            return;
        }
    };

    const removeWidgetFromDashboard = async function (widgetId: string) {
        if (
            selectedDashboardId.value &&
            dashboards.value[selectedDashboardId.value] &&
            dashboards.value[selectedDashboardId.value]?.widgets &&
            dashboards.value[selectedDashboardId.value]?.widgetLayout
        ) {
            isRefetching.value = true;
            const filteredWidgets = dashboards.value[selectedDashboardId.value]?.widgets.filter(
                (widget) => widget.id !== widgetId
            );
            const filteredWidgetLayout = dashboards.value[
                selectedDashboardId.value
            ]?.widgetLayout.filter((widget) => widget.widgetId !== widgetId);
            if (!filteredWidgets || !filteredWidgetLayout) {
                return;
            }

            try {
                await deleteDashboardWidget({
                    widgetId,
                    dashboardId: selectedDashboardId.value,
                    widgetTitle: dashboards.value[selectedDashboardId.value].title,
                });
            } catch (error) {
                console.error('Error removing widget from dashboard:', error);
                isRefetching.value = false;
                return;
            }

            dashboards.value[selectedDashboardId.value] = {
                ...dashboards.value[selectedDashboardId.value],
                widgetLayout: [...filteredWidgetLayout],
                widgets: [...filteredWidgets],
            };
            isRefetching.value = false;
        }
    };

    const updateWidgetComment = async (widgetId: string, comment: string) => {
        if (!selectedDashboardId.value || !dashboards.value[selectedDashboardId.value]) {
            return;
        }

        try {
            const res = await updateDashboardWidgetComment({
                dashboardId: selectedDashboardId.value,
                widgetId,
                comment,
            });
            dashboards.value[selectedDashboardId.value].widgets = dashboards.value[
                selectedDashboardId.value
            ].widgets.map((w) => {
                if (w.id === widgetId) {
                    w.comment = res.comment;
                }
                return w;
            });
            return res;
        } catch (error) {
            console.error('Error updating widget comment:', error);
        }
    };

    const toggleFavorite = async function (dashboardId: string) {
        const idx = items.value.findIndex((i) => i.id === dashboardId);
        if (idx === -1) return;
        const previous = items.value[idx].isFavorite;
        const next = !previous;
        items.value[idx] = { ...items.value[idx], isFavorite: next };
        if (dashboards.value[dashboardId]) {
            dashboards.value[dashboardId].isFavorite = next;
        }
        try {
            await patchDashboardFavorite({ dashboardId, isFavorite: next });
        } catch (error) {
            const rollbackIdx = items.value.findIndex((i) => i.id === dashboardId);
            if (rollbackIdx !== -1) {
                items.value[rollbackIdx] = { ...items.value[rollbackIdx], isFavorite: previous };
            }
            if (dashboards.value[dashboardId]) {
                dashboards.value[dashboardId].isFavorite = previous;
            }
            throw error;
        }
    };

    const reorderDashboards = async function (orderedIds: string[]) {
        const previousSortOrders = new Map(items.value.map((item) => [item.id, item.sortOrder]));
        const byId = new Map(items.value.map((item) => [item.id, item]));

        const uniqueIds = new Set(orderedIds);
        if (uniqueIds.size !== orderedIds.length || uniqueIds.size !== byId.size) return;
        for (const id of uniqueIds) {
            if (!byId.has(id)) return;
        }

        const reordered = orderedIds.map((id, index) => ({
            ...(byId.get(id) as NonNullable<ReturnType<typeof byId.get>>),
            sortOrder: index,
        }));

        items.value = reordered;
        try {
            await patchDashboardSortOrder({
                items: orderedIds.map((dashboardId, sortOrder) => ({ dashboardId, sortOrder })),
            });
        } catch (error) {
            items.value = items.value
                .map((item) => {
                    const prev = previousSortOrders.get(item.id);
                    return prev !== undefined ? { ...item, sortOrder: prev } : item;
                })
                .sort((a, b) => a.sortOrder - b.sortOrder);
            throw error;
        }
    };

    const refreshSelectedDashboard = async function (dashboardId: string) {
        if (!dashboardId) {
            return;
        }
        try {
            await getItem(dashboardId);
        } catch (error) {
            console.error('Error refreshing selected dashboard:', error);
        }
    };

    return {
        items,
        getItems,
        addItem,
        getItem,
        isDashboardOwner,
        copyItem,
        deleteItem,
        widgetFormOpen,
        updateItemTitle,
        updateDashboardWidgetLayout,
        removeWidgetFromDashboard,
        updateWidgetComment,
        toggleFavorite,
        reorderDashboards,
        refreshSelectedDashboard,
        isRefetching,
        isLoadingDashboard,
        isLoadingItems,
        dashboards,
        selectedDashboardId,
        selectedDashboard,
    };
});
